import { useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy 
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { EmailTemplate } from '../types/emailTemplate';
import { useAuth } from '../contexts/AuthContext';

export const useEmailTemplates = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const templatesCollection = collection(db, 'emailTemplates');
      const templatesQuery = query(templatesCollection, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(templatesQuery);
      
      const templatesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as EmailTemplate[];
      
      setTemplates(templatesData);
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  // Create template
  const createTemplate = async (templateData: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>): Promise<string> => {
    try {
      if (!user) throw new Error('User not authenticated');

      const newTemplate = {
        ...templateData,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user.uid,
        updatedBy: user.uid
      };

      const docRef = await addDoc(collection(db, 'emailTemplates'), newTemplate);
      await fetchTemplates(); // Refresh templates
      return docRef.id;
    } catch (err) {
      console.error('Error creating template:', err);
      throw new Error('Failed to create template');
    }
  };

  // Update template
  const updateTemplate = async (id: string, templateData: Partial<EmailTemplate>): Promise<void> => {
    try {
      if (!user) throw new Error('User not authenticated');

      const templateRef = doc(db, 'emailTemplates', id);
      await updateDoc(templateRef, {
        ...templateData,
        updatedAt: new Date(),
        updatedBy: user.uid
      });
      
      await fetchTemplates(); // Refresh templates
    } catch (err) {
      console.error('Error updating template:', err);
      throw new Error('Failed to update template');
    }
  };

  // Delete template
  const deleteTemplate = async (id: string): Promise<void> => {
    try {
      const templateRef = doc(db, 'emailTemplates', id);
      await deleteDoc(templateRef);
      await fetchTemplates(); // Refresh templates
    } catch (err) {
      console.error('Error deleting template:', err);
      throw new Error('Failed to delete template');
    }
  };

  // Get template by type and organization
  const getTemplateByType = (type: EmailTemplate['type'], organizationId?: string): EmailTemplate | null => {
    // First try to find organization-specific template
    if (organizationId) {
      const orgTemplate = templates.find(t => 
        t.type === type && 
        t.organizationId === organizationId && 
        t.isActive
      );
      if (orgTemplate) return orgTemplate;
    }

    // Fall back to default template
    const defaultTemplate = templates.find(t => 
      t.type === type && 
      t.isDefault && 
      t.isActive && 
      !t.organizationId
    );
    
    return defaultTemplate || null;
  };

  // Get active templates by organization
  const getActiveTemplates = (organizationId?: string): EmailTemplate[] => {
    return templates.filter(t => {
      if (!t.isActive) return false;
      if (organizationId) {
        return t.organizationId === organizationId || !t.organizationId;
      }
      return !t.organizationId; // Only global templates if no organization specified
    });
  };

  // Set default template for type
  const setDefaultTemplate = async (templateId: string, type: EmailTemplate['type']): Promise<void> => {
    try {
      // First, remove default flag from all templates of this type
      const templatesOfType = templates.filter(t => t.type === type);
      const updatePromises = templatesOfType.map(template => 
        updateDoc(doc(db, 'emailTemplates', template.id), { isDefault: false })
      );
      await Promise.all(updatePromises);

      // Then set the new default
      await updateDoc(doc(db, 'emailTemplates', templateId), { isDefault: true });
      
      await fetchTemplates(); // Refresh templates
    } catch (err) {
      console.error('Error setting default template:', err);
      throw new Error('Failed to set default template');
    }
  };

  // Toggle template active status
  const toggleTemplateActive = async (id: string, isActive: boolean): Promise<void> => {
    try {
      const templateRef = doc(db, 'emailTemplates', id);
      await updateDoc(templateRef, {
        isActive,
        updatedAt: new Date(),
        updatedBy: user?.uid
      });
      
      await fetchTemplates(); // Refresh templates
    } catch (err) {
      console.error('Error toggling template active:', err);
      throw new Error('Failed to toggle template active status');
    }
  };

  // Initialize templates on mount
  useEffect(() => {
    fetchTemplates();
  }, []);

  return {
    templates,
    loading,
    error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    getTemplateByType,
    getActiveTemplates,
    setDefaultTemplate,
    toggleTemplateActive,
    refetch: fetchTemplates
  };
}; 